'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { MobileMenu } from './MobileMenu'

type Variant = 'home' | 'snacks' | 'racion' | 'gym' | 'products' | 'calculator'

interface HeaderShellContextValue {
  isMenuOpen: boolean
  toggleMenu: () => void
}

const HeaderShellContext = createContext<HeaderShellContextValue | null>(null)

interface HeaderShellProps {
  variant: Variant
  navItems: ReadonlyArray<{ label: string; href: string }>
  activeHref: string
  ctaHref: string
  ctaLabel: string
  children: ReactNode
}

export function HeaderShell({
  variant,
  navItems,
  activeHref,
  ctaHref,
  ctaLabel,
  children,
}: HeaderShellProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  const isSnacks = variant === 'snacks'
  const isProducts = variant === 'products'
  /* Snacks and products pages have a light background — start with a solid dark header so it's visible.
     Racion hero is dark gradient — transparent start is fine like home. */
  const startsOpaque = isSnacks || isProducts
  const headerBgClass = startsOpaque
    ? scrolled && !isMenuOpen
      ? 'bg-deep/95 backdrop-blur-xl shadow-lg shadow-deep/20'
      : scrolled
        ? 'bg-deep'
        : 'bg-deep/90 backdrop-blur-md'
    : scrolled && !isMenuOpen
      ? 'bg-deep/90 backdrop-blur-xl shadow-lg shadow-deep/20'
      : scrolled
        ? 'bg-deep'
        : 'bg-transparent'

  useEffect(() => {
    let ticking = false
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 20)
          ticking = false
        })
        ticking = true
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isMenuOpen])

  const toggleMenu = useCallback(() => setIsMenuOpen((v) => !v), [])
  const closeMenu = useCallback(() => setIsMenuOpen(false), [])

  return (
    <HeaderShellContext.Provider value={{ isMenuOpen, toggleMenu }}>
      <header
        className={`fixed top-0 left-0 right-0 z-70 transition-all duration-500 pt-[env(safe-area-inset-top)] ${headerBgClass}`}
      >
        {children}
      </header>

      <MobileMenu
        isOpen={isMenuOpen}
        onClose={closeMenu}
        navItems={navItems}
        activeHref={activeHref}
        ctaHref={ctaHref}
        ctaLabel={ctaLabel}
      />
    </HeaderShellContext.Provider>
  )
}

export function HamburgerButton() {
  const ctx = useContext(HeaderShellContext)
  if (!ctx) return null
  const { isMenuOpen, toggleMenu } = ctx
  return (
    <button
      type="button"
      onClick={toggleMenu}
      className="lg:hidden text-white w-11 h-11 flex items-center justify-center rounded-xl hover:bg-white/10 transition-colors duration-200 touch-manipulation"
      aria-expanded={isMenuOpen}
      aria-controls="mobile-menu"
      aria-label={isMenuOpen ? 'Закрити меню' : 'Відкрити меню'}
    >
      <div className="w-6 h-5 flex flex-col justify-between" aria-hidden="true">
        <span className={`block h-0.5 bg-white rounded-full transition-all duration-300 origin-center ${isMenuOpen ? 'rotate-45 translate-y-[9px]' : ''}`} />
        <span className={`block h-0.5 bg-white rounded-full transition-all duration-300 ${isMenuOpen ? 'opacity-0 scale-x-0' : ''}`} />
        <span className={`block h-0.5 bg-white rounded-full transition-all duration-300 origin-center ${isMenuOpen ? '-rotate-45 -translate-y-[9px]' : ''}`} />
      </div>
    </button>
  )
}
